import React, { useState, useMemo } from 'react';
import { 
  Receipt, 
  HelpCircle, 
  Percent, 
  TrendingDown, 
  ShieldCheck, 
  Info, 
  ArrowRight, 
  DollarSign, 
  Landmark, 
  FileText, 
  Calculator,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Sparkles
} from 'lucide-react';
import { 
  calculateBrazilImportTaxes, 
  BRAZIL_STATE_ICMS, 
  TaxRegime, 
  ImportTaxBreakdown 
} from '../lib/brazilTax';
import { DEFAULT_CURRENCY_RATES, ShippingCostCalculation } from '../lib/shippingAndPayment';

interface ImportTaxSimulatorProps {
  initialItemsValueJpy?: number;
  selectedShippingMethod?: ShippingCostCalculation | null;
  destinationState?: string;
  onStateChange?: (state: string) => void;
}

export const ImportTaxSimulator: React.FC<ImportTaxSimulatorProps> = ({
  initialItemsValueJpy = 0,
  selectedShippingMethod = null,
  destinationState = 'SP',
  onStateChange
}) => {
  // Input states
  const [currency, setCurrency] = useState<'JPY' | 'USD' | 'BRL'>('JPY');
  const [itemsValueInput, setItemsValueInput] = useState<string>(
    initialItemsValueJpy > 0 ? initialItemsValueJpy.toString() : '10000'
  );
  const [selectedState, setSelectedState] = useState<string>(destinationState || 'SP');
  const [regime, setRegime] = useState<TaxRegime>('remessa_conforme');
  const [includePostalFee, setIncludePostalFee] = useState<boolean>(false);
  const [useCustomShipping, setUseCustomShipping] = useState<boolean>(false);
  const [customShippingUsd, setCustomShippingUsd] = useState<string>('30');
  const [showFormulaDetails, setShowFormulaDetails] = useState<boolean>(false);
  const [showTips, setShowTips] = useState<boolean>(true);

  // Sync state with parent if prop changes
  React.useEffect(() => {
    if (destinationState && destinationState !== selectedState) {
      setSelectedState(destinationState);
    }
  }, [destinationState]);

  // If initialItemsValueJpy changes from parent packages selection, update input
  React.useEffect(() => {
    if (initialItemsValueJpy > 0) {
      if (currency === 'JPY') {
        setItemsValueInput(initialItemsValueJpy.toString());
      } else if (currency === 'USD') {
        setItemsValueInput((initialItemsValueJpy * DEFAULT_CURRENCY_RATES.jpyToUsd).toFixed(2));
      } else if (currency === 'BRL') {
        setItemsValueInput((initialItemsValueJpy * DEFAULT_CURRENCY_RATES.jpyToBrl).toFixed(2));
      }
    }
  }, [initialItemsValueJpy, currency]);

  // Determine active shipping cost in USD
  const activeShippingUsd = useMemo(() => {
    if (useCustomShipping) {
      return parseFloat(customShippingUsd) || 0;
    }
    if (selectedShippingMethod && selectedShippingMethod.isEligible && !selectedShippingMethod.requiresQuote) {
      return selectedShippingMethod.costUsd;
    }
    return 30.00; // default average freight
  }, [useCustomShipping, customShippingUsd, selectedShippingMethod]);

  const numericItemsValue = parseFloat(itemsValueInput) || 0;

  // Compute tax calculation
  const taxResult: ImportTaxBreakdown = useMemo(() => {
    return calculateBrazilImportTaxes({
      itemsValue: numericItemsValue,
      currency,
      shippingCostUsd: activeShippingUsd,
      insuranceUsd: 0,
      stateCode: selectedState,
      regime,
      includePostalFee,
      usdToBrlRate: DEFAULT_CURRENCY_RATES.usdToBrl,
      jpyToUsdRate: DEFAULT_CURRENCY_RATES.jpyToUsd,
      jpyToBrlRate: DEFAULT_CURRENCY_RATES.jpyToBrl,
    });
  }, [numericItemsValue, currency, activeShippingUsd, selectedState, regime, includePostalFee]);

  const handleStateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newState = e.target.value;
    setSelectedState(newState);
    if (onStateChange) {
      onStateChange(newState);
    }
  };

  const handleCurrencyChange = (newCurr: 'JPY' | 'USD' | 'BRL') => {
    if (newCurr === currency) return;
    
    // Convert current input to new currency
    const val = parseFloat(itemsValueInput) || 0;
    let converted = val;

    if (currency === 'JPY') {
      if (newCurr === 'USD') converted = val * DEFAULT_CURRENCY_RATES.jpyToUsd;
      if (newCurr === 'BRL') converted = val * DEFAULT_CURRENCY_RATES.jpyToBrl;
    } else if (currency === 'USD') {
      if (newCurr === 'JPY') converted = val / DEFAULT_CURRENCY_RATES.jpyToUsd;
      if (newCurr === 'BRL') converted = val * DEFAULT_CURRENCY_RATES.usdToBrl;
    } else if (currency === 'BRL') {
      if (newCurr === 'JPY') converted = val / DEFAULT_CURRENCY_RATES.jpyToBrl;
      if (newCurr === 'USD') converted = val / DEFAULT_CURRENCY_RATES.usdToBrl;
    }

    setCurrency(newCurr);
    setItemsValueInput(newCurr === 'JPY' ? Math.round(converted).toString() : converted.toFixed(2));
  };

  return (
    <div id="simulador-impostos-brasil" className="bg-gradient-to-br from-slate-900 via-[#1D3557] to-slate-900 text-white rounded-3xl p-6 shadow-xl border border-slate-700/80 space-y-6">
      
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-700/80 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-400 text-slate-950 rounded-2xl shadow-md shadow-amber-400/20 font-black">
            <Landmark className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-black tracking-tight text-white flex items-center gap-1.5">
                <span>Simulador Oficial de Impostos no Brasil</span>
                <span className="text-xs font-normal text-amber-300 bg-amber-400/20 border border-amber-400/30 px-2 py-0.5 rounded-full">
                  II + ICMS
                </span>
              </h3>
            </div>
            <p className="text-xs text-slate-300">
              Cálculo de tributação aduaneira pela <strong>Lei Federal 14.902/2024</strong> e ICMS por dentro
            </p>
          </div>
        </div>

        {/* Regime Switcher */}
        <div className="flex items-center bg-slate-800/90 p-1 rounded-xl border border-slate-700 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setRegime('remessa_conforme')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 flex-1 sm:flex-none justify-center ${
              regime === 'remessa_conforme'
                ? 'bg-amber-400 text-slate-950 shadow-xs font-black'
                : 'text-slate-300 hover:text-white'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Regime Atual (20% / 60% com Desconto)</span>
          </button>
          <button
            type="button"
            onClick={() => setRegime('traditional')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 flex-1 sm:flex-none justify-center ${
              regime === 'traditional'
                ? 'bg-amber-400 text-slate-950 shadow-xs font-black'
                : 'text-slate-300 hover:text-white'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Regime Tradicional (60% Fixo)</span>
          </button>
        </div>
      </div>

      {/* Main Grid: Inputs & Live Simulation Result */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Form: Inputs (5 Cols) */}
        <div className="lg:col-span-5 space-y-4 bg-slate-800/60 p-4 rounded-2xl border border-slate-700/70">
          
          {/* Purchase / Declared Items Value Input */}
          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5 text-amber-400" />
                <span>Valor Total dos Produtos</span>
              </label>
              <div className="flex items-center gap-1 bg-slate-900/90 p-0.5 rounded-lg border border-slate-700 text-[11px] font-bold">
                <button
                  type="button"
                  onClick={() => handleCurrencyChange('JPY')}
                  className={`px-2 py-0.5 rounded ${currency === 'JPY' ? 'bg-amber-400 text-slate-950 font-black' : 'text-slate-400 hover:text-white'}`}
                >
                  JPY (¥)
                </button>
                <button
                  type="button"
                  onClick={() => handleCurrencyChange('USD')}
                  className={`px-2 py-0.5 rounded ${currency === 'USD' ? 'bg-amber-400 text-slate-950 font-black' : 'text-slate-400 hover:text-white'}`}
                >
                  USD ($)
                </button>
                <button
                  type="button"
                  onClick={() => handleCurrencyChange('BRL')}
                  className={`px-2 py-0.5 rounded ${currency === 'BRL' ? 'bg-amber-400 text-slate-950 font-black' : 'text-slate-400 hover:text-white'}`}
                >
                  BRL (R$)
                </button>
              </div>
            </div>

            <div className="relative">
              <input
                type="number"
                min="0"
                step={currency === 'JPY' ? '100' : '1'}
                value={itemsValueInput}
                onChange={(e) => setItemsValueInput(e.target.value)}
                placeholder="Ex: 10000"
                className="w-full px-4 py-2.5 bg-slate-950 text-white rounded-xl border border-slate-700 font-mono font-black text-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 font-mono">
                {currency === 'JPY' && `≈ US$ ${taxResult.itemsValueUsd.toFixed(2)}`}
                {currency === 'USD' && `≈ ¥ ${taxResult.itemsValueJpy.toLocaleString()}`}
                {currency === 'BRL' && `≈ US$ ${taxResult.itemsValueUsd.toFixed(2)}`}
              </div>
            </div>
            <p className="text-[11px] text-slate-400">
              Valor de compra ou declarado dos itens nas lojas japonesas (Mercari, Amazon JP, etc.).
            </p>
          </div>

          {/* Freight Value Source */}
          <div className="space-y-2 pt-2 border-t border-slate-700/60">
            <div className="flex justify-between items-center text-xs">
              <label className="font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-sky-400" />
                <span>Frete Internacional</span>
              </label>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomShipping}
                  onChange={(e) => setUseCustomShipping(e.target.checked)}
                  className="rounded text-amber-500 focus:ring-0 w-3.5 h-3.5"
                />
                <span>Valor manual</span>
              </label>
            </div>

            {useCustomShipping ? (
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={customShippingUsd}
                  onChange={(e) => setCustomShippingUsd(e.target.value)}
                  placeholder="30.00"
                  className="w-full px-3 py-2 bg-slate-950 text-white rounded-xl border border-slate-700 font-mono font-bold text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">
                  USD ($) ≈ R$ {(parseFloat(customShippingUsd || '0') * DEFAULT_CURRENCY_RATES.usdToBrl).toFixed(2)}
                </span>
              </div>
            ) : (
              <div className="p-2.5 bg-slate-950/80 rounded-xl border border-slate-700/80 flex items-center justify-between text-xs">
                <div>
                  <span className="font-bold text-slate-200">
                    {selectedShippingMethod ? selectedShippingMethod.method.name : 'Média Aérea Padrão'}
                  </span>
                  <span className="block text-[11px] text-slate-400 font-mono">
                    (Incluso na base de cálculo aduaneiro CIF)
                  </span>
                </div>
                <div className="text-right font-mono font-bold text-sky-300">
                  US$ {taxResult.shippingCostUsd.toFixed(2)}
                  <span className="block text-[10px] text-slate-400 font-normal">
                    (R$ {taxResult.shippingCostBrl.toFixed(2)})
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Destination State & ICMS selection */}
          <div className="space-y-1.5 pt-2 border-t border-slate-700/60">
            <div className="flex justify-between items-center">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-300">
                Estado de Destino no Brasil (ICMS)
              </label>
              <span className="text-xs font-bold text-amber-300 font-mono">
                {taxResult.icmsRatePercent}% ICMS
              </span>
            </div>
            <select
              value={selectedState}
              onChange={handleStateSelect}
              className="w-full px-3 py-2 bg-slate-950 text-white rounded-xl border border-slate-700 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              {Object.values(BRAZIL_STATE_ICMS).map((st) => (
                <option key={st.state} value={st.state}>
                  {st.state} — {st.name} ({regime === 'remessa_conforme' ? '17% Unificado' : `${st.standardIcmsPercent}% Padrão`})
                </option>
              ))}
            </select>
          </div>

          {/* Traditional options: Postal Fee */}
          {regime === 'traditional' && (
            <label className="flex items-center gap-2 p-2 bg-slate-900/80 rounded-xl border border-slate-700 text-xs text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={includePostalFee}
                onChange={(e) => setIncludePostalFee(e.target.checked)}
                className="rounded text-amber-500 focus:ring-0"
              />
              <span>Incluir Despacho Postal dos Correios (+ R$ 15,00)</span>
            </label>
          )}

          {/* Exchange Rate Reference Note */}
          <div className="pt-2 text-[11px] text-slate-400 flex items-center justify-between border-t border-slate-700/40">
            <span>Cotação Aduaneira Base:</span>
            <span className="font-mono text-slate-300 font-bold">
              US$ 1 = R$ {DEFAULT_CURRENCY_RATES.usdToBrl.toFixed(2)} | ¥ 1 = R$ {DEFAULT_CURRENCY_RATES.jpyToBrl.toFixed(4)}
            </span>
          </div>

        </div>

        {/* Right Result: Detailed Tax Summary & Cards (7 Cols) */}
        <div className="lg:col-span-7 space-y-4">
          
          {/* Status Badge: Under $50 vs Over $50 USD */}
          <div className={`p-3.5 rounded-2xl border flex items-center justify-between gap-3 ${
            taxResult.isUnder50Usd
              ? 'bg-emerald-950/60 border-emerald-500/50 text-emerald-200'
              : 'bg-amber-950/60 border-amber-500/50 text-amber-200'
          }`}>
            <div className="flex items-center gap-2.5">
              <div className={`p-2 rounded-xl font-black ${taxResult.isUnder50Usd ? 'bg-emerald-500 text-slate-950' : 'bg-amber-400 text-slate-950'}`}>
                <Percent className="w-4 h-4" />
              </div>
              <div>
                <div className="text-xs font-black uppercase tracking-wider">
                  {taxResult.isUnder50Usd ? 'Faixa Reduzida: Até US$ 50,00' : 'Faixa Padrão: Acima de US$ 50,00'}
                </div>
                <div className="text-[11px] opacity-90">
                  {taxResult.isUnder50Usd
                    ? 'Alíquota de 20% no Imposto de Importação (II) + 17% ICMS estadual'
                    : 'Alíquota de 60% com desconto legal de US$ 20 no imposto (Lei 14.902/2024)'}
                </div>
              </div>
            </div>
            <div className="text-right font-mono font-black text-sm">
              CIF US$ {taxResult.cifUsd.toFixed(2)}
              <span className="block text-[10px] opacity-80 font-normal">
                (R$ {taxResult.cifBrl.toFixed(2)})
              </span>
            </div>
          </div>

          {/* Primary Highlight Cards: Impostos vs Custo Total */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            
            {/* Total Taxes Card */}
            <div className="p-4 bg-slate-800/90 rounded-2xl border border-red-500/30 space-y-1">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-red-300 block">
                Total Estimado de Impostos (II + ICMS)
              </span>
              <div className="text-2xl font-black font-mono text-red-400">
                R$ {taxResult.totalTaxesBrl.toFixed(2)}
              </div>
              <div className="text-xs text-slate-300 font-mono">
                ≈ US$ {taxResult.totalTaxesUsd.toFixed(2)}
                <span className="ml-1 text-[11px] text-red-300 font-bold">
                  ({taxResult.effectiveTaxRateOnCifPercent}% sobre CIF)
                </span>
              </div>
            </div>

            {/* Total Landed Cost Card */}
            <div className="p-4 bg-amber-400 text-slate-950 rounded-2xl shadow-lg shadow-amber-400/10 space-y-1">
              <span className="text-[10px] font-black uppercase tracking-wider text-slate-900 block">
                Custo Total Final da Importação
              </span>
              <div className="text-2xl font-black font-mono text-slate-950">
                R$ {taxResult.totalLandedCostBrl.toFixed(2)}
              </div>
              <div className="text-xs text-slate-900 font-bold font-mono">
                Produtos + Frete + Impostos (≈ US$ {taxResult.totalLandedCostUsd.toFixed(2)})
              </div>
            </div>

          </div>

          {/* Itemized Tax Breakdown Table */}
          <div className="bg-slate-950/70 p-4 rounded-2xl border border-slate-800 space-y-2.5 text-xs">
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block pb-1 border-b border-slate-800">
              Detalhamento dos Valores Aduaneiros & Tributos
            </span>

            {/* 1. CIF Value */}
            <div className="flex justify-between items-center text-slate-300">
              <span>1. Base Aduaneira CIF (Produtos + Frete):</span>
              <span className="font-mono font-bold text-white">
                R$ {taxResult.cifBrl.toFixed(2)} <span className="text-slate-400 text-[11px]">(US$ {taxResult.cifUsd.toFixed(2)})</span>
              </span>
            </div>

            {/* 2. Federal Import Tax */}
            <div className="flex justify-between items-center text-slate-300">
              <div>
                <span>2. Imposto de Importação Federal (II):</span>
                <span className="ml-1.5 px-1.5 py-0.2 bg-blue-900/60 text-blue-300 rounded text-[10px] font-bold">
                  {taxResult.iiRatePercent}% nominal
                  {taxResult.iiDeductionUsd > 0 && ` (-US$ ${taxResult.iiDeductionUsd.toFixed(2)})`}
                </span>
              </div>
              <span className="font-mono font-bold text-amber-300">
                R$ {taxResult.iiValueBrl.toFixed(2)} <span className="text-slate-400 text-[11px]">(US$ {taxResult.iiValueUsd.toFixed(2)})</span>
              </span>
            </div>

            {/* 3. State ICMS */}
            <div className="flex justify-between items-center text-slate-300">
              <div>
                <span>3. ICMS Estadual ({selectedState}):</span>
                <span className="ml-1.5 px-1.5 py-0.2 bg-purple-900/60 text-purple-300 rounded text-[10px] font-bold">
                  {taxResult.icmsRatePercent}% por dentro
                </span>
              </div>
              <span className="font-mono font-bold text-purple-300">
                R$ {taxResult.icmsValueBrl.toFixed(2)} <span className="text-slate-400 text-[11px]">(US$ {taxResult.icmsValueUsd.toFixed(2)})</span>
              </span>
            </div>

            {/* 4. Postal Fee */}
            {taxResult.postalFeeBrl > 0 && (
              <div className="flex justify-between items-center text-slate-300">
                <span>4. Despacho Postal (Correios):</span>
                <span className="font-mono font-bold text-slate-200">
                  R$ {taxResult.postalFeeBrl.toFixed(2)}
                </span>
              </div>
            )}

            {/* Total Line */}
            <div className="pt-2 border-t border-slate-800 flex justify-between items-center font-bold text-sm">
              <span className="text-white">Total a pagar de Impostos no Brasil:</span>
              <span className="font-mono text-red-400 font-black">
                R$ {taxResult.totalTaxesBrl.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Toggle for Math Details & Explanations */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowFormulaDetails(!showFormulaDetails)}
              className="text-xs text-amber-300 hover:text-amber-200 font-bold flex items-center gap-1 transition-colors"
            >
              <span>Como é calculado o ICMS por dentro e o Imposto de Importação?</span>
              {showFormulaDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>

            {showFormulaDetails && (
              <div className="p-3 bg-slate-950/90 rounded-xl border border-slate-800 text-[11px] text-slate-300 space-y-2">
                <div>
                  <strong className="text-amber-300">1. Imposto de Importação (II):</strong>
                  <p className="mt-0.5 text-slate-400">
                    Calculado sobre o valor aduaneiro CIF (Produtos + Frete). Compras até US$ 50,00 pagam 20%. Compras acima de US$ 50,00 pagam 60% com dedução de US$ 20,00 fixos no valor final do imposto.
                  </p>
                </div>
                <div>
                  <strong className="text-purple-300">2. ICMS Estadual (Cálculo Oficial "Por Dentro"):</strong>
                  <p className="mt-0.5 text-slate-400 font-mono">
                    Base ICMS = (Valor Aduaneiro + II) ÷ (1 - {taxResult.icmsRatePercent / 100}) = R$ {taxResult.icmsBaseBrl.toFixed(2)}
                  </p>
                  <p className="text-slate-400 font-mono">
                    Valor ICMS = R$ {taxResult.icmsBaseBrl.toFixed(2)} × {taxResult.icmsRatePercent}% = R$ {taxResult.icmsValueBrl.toFixed(2)}
                  </p>
                </div>
              </div>
            )}
          </div>

        </div>

      </div>

      {/* Helpful Customs Tips & Advice */}
      <div className="p-3.5 bg-blue-950/50 rounded-2xl border border-blue-800/40 text-xs text-blue-200 flex items-start gap-2.5">
        <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-bold text-white">
            💡 Dicas Importantes para Importação do Japão:
          </p>
          <ul className="list-disc list-inside space-y-0.5 text-blue-200/90 text-[11px]">
            <li>
              O imposto aduaneiro é recolhido pelo ambiente <strong>Minhas Importações dos Correios</strong> ou diretamente pela transportadora (FedEx/DHL) quando a encomenda chega ao Brasil.
            </li>
            <li>
              Certifique-se de que o seu <strong>CPF</strong> cadastrado na suíte esteja regular junto à Receita Federal para evitar atrasos na liberação alfandegária.
            </li>
            <li>
              Você pode consolidar vários produtos em uma única caixa para economizar no frete internacional e otimizar a declaração aduaneira.
            </li>
          </ul>
        </div>
      </div>

    </div>
  );
};

export default ImportTaxSimulator;
